import type { MouseEvent, ReactNode, UIEvent } from "react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDrag } from "@use-gesture/react";
import {
  Archive,
  Bell,
  CheckSquare,
  CheckCheck,
  ChevronRight,
  LogOut,
  Menu,
  MessageCircle,
  Moon,
  MoreVertical,
  PanelLeftClose,
  Pin,
  RefreshCw,
  Search,
  Settings,
  Sparkles,
  StickyNote,
  Sun,
  Trash2,
  X
} from "lucide-react";
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router";
import { api, guardedRecentPinMutation, type RecentItem, type User } from "../api/client";
import {
  clearPendingOrdinaryNoteCreate,
  createOfflineStorageScope,
  createOrdinaryNoteWithIntent,
  ordinaryNoteCreateIntentDisposition,
  OrdinaryNoteCreateIntentError,
  readPendingOrdinaryNoteCreate
} from "./offlineNotes";
import { setUnreadAppBadge } from "../pwa/badge";
import { isPhoneLayout, watchPhoneLayout } from "./responsive";
import { NOTIFICATION_ACK, NOTIFICATION_NAVIGATE, NOTIFICATION_READY, validNotificationTarget } from "../pwa/notification-navigation";

type Home = "notes" | "actions" | "chat" | "skills" | "settings";

export type ChatTopControls = {
  title: string;
  status: string;
  model: string;
  modelOptions: Array<{ id: string; label: string }>;
  disabled?: boolean;
  pinned?: boolean;
  archived?: boolean;
  onModelChange: (model: string) => void;
  onPinToggle: () => void;
  onArchiveToggle: () => void;
  onRefresh: () => void;
  onTitleChange: (title: string) => void;
  onTitleSave: () => void;
};

const NotesWorkspace = lazy(() => import("./NotesWorkspace").then((module) => ({ default: module.NotesWorkspace })));
const ChatWorkspace = lazy(() => import("./ChatWorkspace").then((module) => ({ default: module.ChatWorkspace })));
const ActionsWorkspace = lazy(() => import("./ActionsWorkspace").then((module) => ({ default: module.ActionsWorkspace })));
const SkillsWorkspace = lazy(() => import("./SkillsWorkspace").then((module) => ({ default: module.SkillsWorkspace })));
const MemoryWorkspace = lazy(() => import("./MemoryWorkspace").then((module) => ({ default: module.MemoryWorkspace })));
const SettingsWorkspace = lazy(() => import("./SettingsWorkspace").then((module) => ({ default: module.SettingsWorkspace })));
const SourcesWorkspace = lazy(() => import("./SourcesWorkspace").then((module) => ({ default: module.SourcesWorkspace })));
const WelcomeWorkspace = lazy(() => import("./WelcomeWorkspace").then((module) => ({ default: module.WelcomeWorkspace })));
const JobMonitor = lazy(() => import("./JobMonitor").then((module) => ({ default: module.JobMonitor })));

const navItems: Array<{ id: Home; title: string; to: string; Icon: typeof StickyNote }> = [
  { id: "notes", title: "Notes", to: "/notes", Icon: StickyNote },
  { id: "actions", title: "Actions", to: "/actions", Icon: CheckSquare },
  { id: "skills", title: "Skills", to: "/skills", Icon: Sparkles },
  { id: "settings", title: "Settings", to: "/settings", Icon: Settings }
];

export function App() {
  useButtonTooltips();
  const queryClient = useQueryClient();
  const auth = useQuery({ queryKey: ["auth"], queryFn: api.authStatus });
  const params = new URLSearchParams(window.location.search);
  const inviteToken = params.get("invite") || "";
  const resetToken = params.get("reset") || "";
  const invite = useQuery({ queryKey: ["invite", inviteToken], queryFn: () => api.getInvite(inviteToken), enabled: Boolean(inviteToken) });
  const login = useMutation({
    mutationFn: ({ email, password, remember }: { email: string; password: string; remember: boolean }) =>
      api.login(email, password, remember),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth"] })
  });

  const finishAuthFlow = () => {
    window.history.replaceState({}, "", "/");
    void queryClient.invalidateQueries({ queryKey: ["auth"] });
  };

  if (auth.isLoading) return <Splash />;
  if (auth.data?.setupRequired) return <AuthFrame title="Admin setup needed" message="Create the first admin from the server environment, then return here." />;
  if (inviteToken) return <InviteView token={inviteToken} invite={invite.data?.invite} loading={invite.isLoading} loadError={invite.error} onComplete={finishAuthFlow} />;
  if (resetToken) return <ResetPasswordView token={resetToken} onComplete={finishAuthFlow} />;
  if (!auth.data?.user) return <LoginView login={login.mutateAsync} error={login.error} loading={login.isPending} />;
  return <AppShell user={auth.data.user} />;
}

function useButtonTooltips() {
  useEffect(() => {
    const setTooltip = (button: HTMLElement) => {
      if (button.title && button.dataset.autoTooltip !== "true") return;
      const label = (button.getAttribute("aria-label") || button.textContent || "").replace(/\s+/g, " ").trim();
      if (!label) return;
      button.title = label.length > 120 ? `${label.slice(0, 117)}…` : label;
      button.dataset.autoTooltip = "true";
    };
    const apply = (root: ParentNode) => {
      if (root instanceof HTMLElement && root.matches("button, [role='button']")) setTooltip(root);
      for (const button of root.querySelectorAll<HTMLElement>("button, [role='button']")) setTooltip(button);
    };
    apply(document);
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        const target = record.target instanceof Element ? record.target : record.target.parentElement;
        if (target) apply(target.closest("button, [role='button']") || target);
      }
    });
    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}

function Splash() {
  return (
    <main className="app-splash">
      <Wordmark />
      <div className="loading-bar" />
    </main>
  );
}

function AuthFrame({ title, message }: { title: string; message: string }) {
  return (
    <main className="auth-screen">
      <section className="auth-panel">
        <Wordmark />
        <h1>{title}</h1>
        <p>{message}</p>
      </section>
    </main>
  );
}

function LoginView({ login, error, loading }: {
  login: (input: { email: string; password: string; remember: boolean }) => Promise<unknown>;
  error: Error | null;
  loading: boolean;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [resetStatus, setResetStatus] = useState("");
  const resetRequest = useMutation({
    mutationFn: () => api.requestPasswordReset(email.trim()),
    onSuccess: () => setResetStatus("If that account exists, a reset link has been sent."),
    onError: (resetError) => setResetStatus(resetError instanceof Error ? resetError.message : "Could not request a reset link.")
  });

  return (
    <main className="auth-screen">
      <form className="auth-panel" onSubmit={(event) => {
        event.preventDefault();
        void login({ email, password, remember });
      }}>
        <Wordmark />
        <h1>Welcome back</h1>
        <label>
          <span>Email</span>
          <input autoComplete="email" inputMode="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </label>
        <label>
          <span>Password</span>
          <input autoComplete="current-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
        </label>
        <label className="check-row">
          <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
          <span>Keep me signed in</span>
        </label>
        {error && <p className="error-text">{error.message}</p>}
        <button className="primary-button" disabled={loading}>{loading ? "Signing in..." : "Sign in"}</button>
        <button className="link-button auth-link-button" type="button" disabled={!email.trim() || resetRequest.isPending} onClick={() => resetRequest.mutate()}>
          {resetRequest.isPending ? "Requesting…" : "Forgot password?"}
        </button>
        {resetStatus && <p className="auth-status">{resetStatus}</p>}
      </form>
    </main>
  );
}

function InviteView({ token, invite, loading, loadError, onComplete }: {
  token: string;
  invite?: { email?: string; role?: string };
  loading: boolean;
  loadError: Error | null;
  onComplete: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const register = useMutation({ mutationFn: () => api.register(token, invite?.email || email, password), onSuccess: onComplete });
  if (loading) return <Splash />;
  if (loadError || !invite) return <AuthFrame title="Invitation unavailable" message={loadError?.message || "This invitation is invalid or expired."} />;
  const mismatch = Boolean(confirm) && password !== confirm;
  return (
    <main className="auth-screen">
      <form className="auth-panel" onSubmit={(event) => { event.preventDefault(); if (!mismatch) register.mutate(); }}>
        <Wordmark />
        <h1>Join Notey-G</h1>
        <p className="muted">Create your {invite.role || "member"} account.</p>
        <label><span>Email</span><input autoComplete="email" type="email" value={invite.email || email} disabled={Boolean(invite.email)} onChange={(event) => setEmail(event.target.value)} /></label>
        <label><span>Password</span><input autoComplete="new-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <label><span>Confirm password</span><input autoComplete="new-password" type="password" value={confirm} onChange={(event) => setConfirm(event.target.value)} /></label>
        {mismatch && <p className="error-text">Passwords do not match.</p>}
        {register.error && <p className="error-text">{register.error.message}</p>}
        <button className="primary-button" disabled={register.isPending || mismatch || password.length < 8 || !(invite.email || email.trim())}>{register.isPending ? "Creating account…" : "Create account"}</button>
      </form>
    </main>
  );
}

function ResetPasswordView({ token, onComplete }: { token: string; onComplete: () => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const reset = useMutation({ mutationFn: () => api.completePasswordReset(token, password), onSuccess: onComplete });
  const mismatch = Boolean(confirm) && password !== confirm;
  return (
    <main className="auth-screen">
      <form className="auth-panel" onSubmit={(event) => { event.preventDefault(); if (!mismatch) reset.mutate(); }}>
        <Wordmark />
        <h1>Choose a new password</h1>
        <label><span>New password</span><input autoComplete="new-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <label><span>Confirm password</span><input autoComplete="new-password" type="password" value={confirm} onChange={(event) => setConfirm(event.target.value)} /></label>
        {mismatch && <p className="error-text">Passwords do not match.</p>}
        {reset.error && <p className="error-text">{reset.error.message}</p>}
        <button className="primary-button" disabled={reset.isPending || mismatch || password.length < 8}>{reset.isPending ? "Updating…" : "Update password"}</button>
      </form>
    </main>
  );
}

function AppShell({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const drawerRef = useRef<HTMLElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const markAllReadButtonRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedBeforeDrawer = useRef<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(() => localStorage.getItem("noteyg.app.drawer") !== "closed");
  const [drawerModal, setDrawerModal] = useState(isPhoneLayout);
  const [drawerOffset, setDrawerOffset] = useState<number | null>(null);
  const [dark, setDark] = useState(() => localStorage.getItem("noteyg.app.theme") === "dark");
  const [chatTopControls, setChatTopControls] = useState<ChatTopControls | null>(null);
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const [readAllConfirmOpen, setReadAllConfirmOpen] = useState(false);
  const [createRecoveryStatus, setCreateRecoveryStatus] = useState("");
  const offlineScope = useMemo(
    () => createOfflineStorageScope(
      user.id,
      typeof window !== "undefined" ? window.location.origin : ""
    ),
    [user.id]
  );

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data || {};
      if (data.type !== NOTIFICATION_NAVIGATE || !validNotificationTarget(data.url)) return;
      navigate(data.url);
      navigator.serviceWorker.controller?.postMessage({ type: NOTIFICATION_ACK, url: data.url });
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    void navigator.serviceWorker.ready.then((registration) => {
      (navigator.serviceWorker.controller || registration.active)?.postMessage({ type: NOTIFICATION_READY });
    });
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);
  const recent = useInfiniteQuery({
    queryKey: ["recent"],
    initialPageParam: "",
    queryFn: ({ pageParam }) => api.listRecent(24, pageParam),
    getNextPageParam: (lastPage) => lastPage.page?.afterCursor || lastPage.page?.nextCursor || undefined
  });
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const jobs = useQuery({ queryKey: ["jobs", "shell"], queryFn: () => api.listJobSummaries(20), refetchInterval: 15_000 });
  const approvals = useQuery({ queryKey: ["approvals", "pending", "shell"], queryFn: api.listPendingApprovals, refetchInterval: 15_000 });
  const ordinaryCreateEnabled = recent.data?.pages?.[0]?.workspaceMetadataMutations?.enabled === true
    && recent.data.pages[0].workspaceMetadataMutations.operations.includes("note.create");
  const createNote = useMutation({
    mutationFn: async () => {
      if (offlineScope && ordinaryCreateEnabled) {
        return createOrdinaryNoteWithIntent(offlineScope, {
          title: "New note",
          body: "",
          folderId: "",
          tags: [],
          options: {}
        });
      }
      return { response: await api.createNote("New note", ""), intent: null };
    },
    onSuccess: async (result) => {
      if (offlineScope && result.intent) {
        await clearPendingOrdinaryNoteCreate(
          offlineScope,
          result.intent.idempotencyKey
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["recent"] });
      setCreateRecoveryStatus("");
      navigate(`/notes/${result.response.note?.id || "new"}`);
    },
    onError: async error => {
      if (!(error instanceof OrdinaryNoteCreateIntentError) || !offlineScope) return;
      if (ordinaryNoteCreateIntentDisposition(error) === "definitive-no-commit") {
        await clearPendingOrdinaryNoteCreate(
          offlineScope,
          error.intent.idempotencyKey
        );
        setCreateRecoveryStatus(error.message);
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notes", "tree"] }),
        queryClient.invalidateQueries({ queryKey: ["recent"] })
      ]);
      setCreateRecoveryStatus(
        "The earlier note may already exist. Notes was refreshed; use New note again to recover it safely."
      );
    }
  });
  const createChat = useMutation({
    mutationFn: (contextNoteId?: string) => api.createChatThread(contextNoteId ? { contextNoteId } : {}),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["recent"] });
      navigate(`/chat/${data.thread?.id || "new"}`);
    }
  });
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      void setUnreadAppBadge(0);
      return queryClient.invalidateQueries({ queryKey: ["auth"] });
    }
  });
  const markAllRead = useMutation({
    mutationFn: api.markAllChatThreadsRead,
    onSuccess: async () => {
      await setUnreadAppBadge(0);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["recent"] }),
        queryClient.invalidateQueries({ queryKey: ["chat"] })
      ]);
    }
  });
  const bindDrawerDrag = useDrag(({ active, offset: [x], last, velocity: [vx], direction: [dx] }) => {
    const width = drawerRef.current?.getBoundingClientRect().width || 336;
    setDrawerOffset(Math.min(0, Math.max(-width, x)));
    if (last) {
      const shouldOpen = x > -width * 0.52 || (vx > 0.35 && dx > 0);
      setDrawerOpen(shouldOpen);
      setDrawerOffset(null);
    }
    if (!active) setDrawerOffset(null);
  }, {
    axis: "x",
    bounds: () => {
      const width = drawerRef.current?.getBoundingClientRect().width || 336;
      return { left: -width, right: 0 };
    },
    filterTaps: true,
    from: () => {
      const width = drawerRef.current?.getBoundingClientRect().width || 336;
      return [drawerOpen ? 0 : -width, 0];
    },
    pointer: { touch: true },
    rubberband: 0.08
  });

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("noteyg.app.theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    localStorage.setItem("noteyg.app.drawer", drawerOpen ? "open" : "closed");
  }, [drawerOpen]);

  useEffect(() => {
    const events = new EventSource("/api/chat/summary/events");
    const onSummary = (event: MessageEvent) => {
      try {
        const summary = JSON.parse(event.data) as { unreadTotal?: number };
        void setUnreadAppBadge(Number(summary.unreadTotal || 0));
        void queryClient.invalidateQueries({ queryKey: ["recent"] });
        void queryClient.invalidateQueries({ queryKey: ["approvals"] });
        void queryClient.invalidateQueries({ queryKey: ["jobs"] });
      } catch {
        /* wait for the next valid summary */
      }
    };
    events.addEventListener("summary", onSummary as EventListener);
    return () => events.close();
  }, [queryClient]);

  useEffect(() => {
    if (location.pathname.startsWith("/chat")) return;
    setChatTopControls(null);
    setChatMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => watchPhoneLayout(() => setDrawerModal(isPhoneLayout())), []);

  const openDrawer = useCallback(() => {
    lastFocusedBeforeDrawer.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(({ restoreFocus = true }: { restoreFocus?: boolean } = {}) => {
    setDrawerOpen(false);
    setDrawerOffset(null);
    if (!restoreFocus) return;
    window.requestAnimationFrame(() => {
      const target = lastFocusedBeforeDrawer.current || menuButtonRef.current;
      if (target && document.contains(target)) target.focus();
    });
  }, []);

  const focusMainContent = useCallback(() => {
    mainRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!drawerOpen || !drawerModal) return;
    const drawer = drawerRef.current;
    if (!drawer) return;
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(",");
    const focusableItems = () =>
      Array.from(drawer.querySelectorAll<HTMLElement>(selector)).filter((element) => element.tabIndex !== -1);

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (previousFocus && !drawer.contains(previousFocus)) lastFocusedBeforeDrawer.current = previousFocus;

    window.requestAnimationFrame(() => {
      const target = focusableItems()[0] || drawer;
      target.focus();
    });

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusableItems();
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    drawer.addEventListener("keydown", onKey);
    return () => drawer.removeEventListener("keydown", onKey);
  }, [drawerModal, drawerOpen, closeDrawer]);

  const activeTitle = useMemo(() => {
    if (location.pathname.startsWith("/welcome")) return "Welcome";
    const item = navItems.find((candidate) => location.pathname.startsWith(candidate.to));
    return item?.title || "Notes";
  }, [location.pathname]);
  const chatRouteActive = location.pathname.startsWith("/chat");
  const welcomeRouteActive = location.pathname.startsWith("/welcome");
  const shellClass = `app-shell ${drawerOpen ? "drawer-open" : "drawer-closed"} ${chatRouteActive ? "chat-shell" : ""}`;
  const routeHostClass = `route-host ${chatRouteActive ? "chat-route-host" : ""} ${welcomeRouteActive ? "welcome-route-host" : ""}`;
  const onboardingRequired = settings.data?.settings?.onboardingComplete === false;

  const recentItems = useMemo(
    () => (recent.data?.pages || []).flatMap((page) => page.items || page.recent || []),
    [recent.data]
  );
  const guardedRecentMetadataEnabled = recent.data?.pages?.[0]?.workspaceMetadataMutations?.enabled === true
    && recent.data.pages[0].workspaceMetadataMutations.operations.includes("note.update");

  useEffect(() => {
    if (!offlineScope || !ordinaryCreateEnabled) return;
    let disposed = false;
    void readPendingOrdinaryNoteCreate(offlineScope).then(intent => {
      if (disposed || !intent) return;
      setCreateRecoveryStatus(
        "An earlier note creation is ready for safe recovery. Refresh Notes, then retry New note."
      );
      void queryClient.invalidateQueries({ queryKey: ["notes", "tree"] });
      void queryClient.invalidateQueries({ queryKey: ["recent"] });
    }).catch(() => {});
    return () => { disposed = true; };
  }, [offlineScope, ordinaryCreateEnabled, queryClient]);
  const activeJobCount = (jobs.data?.jobs || []).filter((job) => ["queued", "running", "needs_approval"].includes(String(job.status || ""))).length;
  const pendingApprovalCount = approvals.data?.approvals.length || 0;
  const jobChipCount = pendingApprovalCount || activeJobCount;

  const loadMoreRecentIfNeeded = useCallback((element: HTMLElement) => {
    if (!recent.hasNextPage || recent.isFetchingNextPage) return;
    const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remaining < 140) void recent.fetchNextPage();
  }, [recent]);

  const handleDrawerScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    loadMoreRecentIfNeeded(event.currentTarget);
  }, [loadMoreRecentIfNeeded]);

  useEffect(() => {
    if (!drawerOpen) return;
    const scroller = drawerRef.current?.querySelector<HTMLElement>(".drawer-scroll");
    if (scroller) loadMoreRecentIfNeeded(scroller);
  }, [drawerOpen, recentItems.length, loadMoreRecentIfNeeded]);

  function openNewNote() {
    closeDrawer({ restoreFocus: false });
    void createNote.mutateAsync();
  }

  function openNewChat() {
    const contextNoteId = noteIdFromPath(location.pathname);
    closeDrawer({ restoreFocus: false });
    void createChat.mutateAsync(contextNoteId);
  }

  function closeReadAllConfirm() {
    setReadAllConfirmOpen(false);
    window.requestAnimationFrame(() => markAllReadButtonRef.current?.focus());
  }

  return (
    <div className={shellClass}>
      <a className="skip-link" href="#app-main" onClick={(event) => {
        event.preventDefault();
        focusMainContent();
      }}>Skip to workspace</a>
      <aside
        {...bindDrawerDrag()}
        id="app-drawer"
        ref={drawerRef}
        className="drawer"
        aria-label="Main navigation"
        aria-modal={drawerModal && drawerOpen ? true : undefined}
        aria-hidden={drawerModal && !drawerOpen ? true : undefined}
        inert={drawerModal && !drawerOpen ? true : undefined}
        role={drawerModal && drawerOpen ? "dialog" : undefined}
        tabIndex={drawerModal ? -1 : undefined}
        style={drawerOffset === null ? undefined : { transform: `translate3d(${drawerOffset}px, 0, 0)` }}
      >
        <div className="drawer-header">
          <Wordmark />
          <button className="icon-button" aria-label="Search notes and conversations" onClick={() => {
            closeDrawer({ restoreFocus: false });
            navigate("/search");
          }}>
            <Search size={19} />
          </button>
          <button className="icon-button" aria-label="Close navigation" onClick={() => closeDrawer()}>
            <PanelLeftClose size={19} />
          </button>
        </div>
        <div className="drawer-scroll" onScroll={handleDrawerScroll}>
          <div className="quick-row">
            <button disabled={createNote.isPending} onClick={openNewNote}><StickyNote size={17} />New note</button>
            <button disabled={createChat.isPending} onClick={openNewChat}><Sparkles size={17} />New chat</button>
          </div>
          {createRecoveryStatus && <p className="muted pad">{createRecoveryStatus}</p>}
          <nav className="drawer-nav">
            {navItems.map(({ to, title, Icon }) => (
              <NavLink key={to} to={to} onClick={() => closeDrawer({ restoreFocus: false })}>
                <Icon size={19} />
                <span>{title}</span>
              </NavLink>
            ))}
          </nav>
          <section className="recent-section">
            <div className="section-title">
              <span>Recent</span>
              <div className="section-title-actions">
                <button ref={markAllReadButtonRef} className="icon-button tiny" disabled={markAllRead.isPending} aria-label="Mark all messages as read" onClick={() => setReadAllConfirmOpen(true)}>
                  <CheckCheck size={14} />
                </button>
                <button className="icon-button tiny" aria-label="Refresh recent" onClick={() => void recent.refetch()}>
                  <RefreshCw size={14} />
                </button>
              </div>
            </div>
            <div className="recent-list">
              {recent.isLoading && <p className="muted pad">Loading recent...</p>}
              {recent.error && <p className="muted pad">Could not load recent.</p>}
              {!recent.isLoading && !recentItems.length && <p className="muted pad">No recent notes or chats</p>}
              {recentItems.map((item) => (
                <RecentRow
                  key={`${kindOf(item)}:${item.id}`}
                  item={item}
                  guardedMetadataEnabled={guardedRecentMetadataEnabled}
                  close={() => closeDrawer({ restoreFocus: false })}
                />
              ))}
              {recent.isFetchingNextPage && <p className="muted pad recent-load-state">Loading more...</p>}
              {!recent.isLoading && recentItems.length > 0 && !recent.hasNextPage && <p className="muted pad recent-load-state">End of recent</p>}
            </div>
          </section>
        </div>
        <footer className="drawer-footer">
          <div className="drawer-user-row">
            <span title={user.email}>{user.email}</span>
            <NavLink className="icon-button" to="/settings" aria-label="Settings" onClick={() => closeDrawer({ restoreFocus: false })}>
              <Settings size={18} />
            </NavLink>
          </div>
          <div className="drawer-footer-actions">
            <button onClick={() => void logout.mutateAsync()}><LogOut size={15} />Sign out</button>
          </div>
        </footer>
      </aside>
      <button className="backdrop" aria-label="Close navigation" aria-controls="app-drawer" onClick={() => closeDrawer()} />
      <button {...bindDrawerDrag()} className="drawer-edge" aria-label="Swipe or tap to open navigation" aria-controls="app-drawer" aria-expanded={drawerOpen} onClick={() => openDrawer()} />
      <main id="app-main" ref={mainRef} className="main-surface" tabIndex={-1}>
        <header className="top-bar">
          <button ref={menuButtonRef} className="icon-button" aria-label="Open navigation" aria-controls="app-drawer" aria-expanded={drawerOpen} onClick={() => openDrawer()}>
            <Menu size={21} />
          </button>
          <div className="top-title">
            <span className="top-kicker">{chatTopControls ? chatTopControls.status || "Conversation" : "Notey-G"}</span>
            {chatTopControls ? (
              <input
                className="chat-title-input top-chat-title-input"
                value={chatTopControls.title}
                disabled={chatTopControls.disabled}
                aria-label="Chat title"
                onChange={(event) => chatTopControls.onTitleChange(event.target.value)}
                onBlur={() => chatTopControls.onTitleSave()}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                }}
              />
            ) : (
              <h1>{activeTitle}</h1>
            )}
          </div>
          <div className="top-actions">
            <button className="icon-button" aria-label={dark ? "Use light mode" : "Use dark mode"} onClick={() => setDark(!dark)}>
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button className="job-chip" onClick={() => navigate("/jobs")}>
              <Bell size={16} />
              <span>Jobs</span>
              {jobChipCount > 0 && <em>{jobChipCount}</em>}
            </button>
            {chatTopControls && (
              <div className="top-overflow">
                <button
                  className="icon-button"
                  aria-label="Chat options"
                  aria-haspopup="menu"
                  aria-expanded={chatMenuOpen}
                  onClick={() => setChatMenuOpen((open) => !open)}
                >
                  <MoreVertical size={18} />
                </button>
                {chatMenuOpen && (
                  <div className="top-overflow-menu" role="menu">
                    <label>
                      <span>Model</span>
                      <select
                        value={chatTopControls.model}
                        disabled={chatTopControls.disabled}
                        onChange={(event) => chatTopControls.onModelChange(event.target.value)}
                      >
                        {chatTopControls.modelOptions.map((model) => (
                          <option key={model.id} value={model.id}>{model.label}</option>
                        ))}
                      </select>
                    </label>
                    <button role="menuitem" disabled={chatTopControls.disabled} onClick={() => {
                      chatTopControls.onPinToggle();
                      setChatMenuOpen(false);
                    }}>
                      <Pin size={15} fill={chatTopControls.pinned ? "currentColor" : "none"} />
                      <span>{chatTopControls.pinned ? "Unpin chat" : "Pin chat"}</span>
                    </button>
                    <button role="menuitem" disabled={chatTopControls.disabled} onClick={() => {
                      chatTopControls.onArchiveToggle();
                      setChatMenuOpen(false);
                    }}>
                      <Archive size={15} />
                      <span>{chatTopControls.archived ? "Restore chat" : "Archive chat"}</span>
                    </button>
                    <button role="menuitem" onClick={() => {
                      chatTopControls.onRefresh();
                      setChatMenuOpen(false);
                    }}>
                      <RefreshCw size={15} />
                      <span>Refresh chat</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </header>
        <div className={routeHostClass}>
          <Suspense fallback={<RouteLoading />}>
            {settings.isLoading ? <RouteLoading /> : onboardingRequired && !welcomeRouteActive ? (
              <Navigate to="/welcome" replace />
            ) : (
              <Routes>
                <Route path="/" element={<StartupRoute loading={false} startScreen={settings.data?.settings?.startScreen} />} />
                <Route path="/welcome" element={<WelcomeWorkspace />} />
                <Route path="/notes/*" element={<NotesWorkspace />} />
                <Route path="/chat/*" element={<ChatWorkspace setTopControls={setChatTopControls} />} />
                <Route path="/actions" element={<ActionsWorkspace />} />
                <Route path="/skills/*" element={<SkillsWorkspace />} />
                <Route path="/memory" element={<MemoryWorkspace />} />
                <Route path="/settings" element={<SettingsWorkspace />} />
                <Route path="/settings/sources" element={<SourcesWorkspace />} />
                <Route path="/jobs" element={<JobMonitor />} />
                <Route path="/search" element={<SearchHome />} />
                <Route path="*" element={<Navigate to="/notes" replace />} />
              </Routes>
            )}
          </Suspense>
        </div>
      </main>
      {readAllConfirmOpen && (
        <ConfirmReadAllDialog
          pending={markAllRead.isPending}
          error={markAllRead.error}
          onCancel={closeReadAllConfirm}
          onConfirm={async () => {
            try {
              await markAllRead.mutateAsync();
              closeReadAllConfirm();
            } catch {
              /* mutation state keeps the dialog open and displays the error */
            }
          }}
        />
      )}
    </div>
  );
}

function ConfirmReadAllDialog({ pending, error, onCancel, onConfirm }: {
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, pending]);

  return (
    <div className="confirm-dialog-backdrop" role="presentation" onMouseDown={() => !pending && onCancel()}>
      <section className="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="read-all-confirm-title" aria-describedby="read-all-confirm-description" onMouseDown={(event) => event.stopPropagation()}>
        <div className="confirm-dialog-icon"><CheckCheck size={22} /></div>
        <h2 id="read-all-confirm-title">Mark all messages as read?</h2>
        <p id="read-all-confirm-description">This will clear every unread chat and notification, including archived conversations.</p>
        {error && <p className="error-text">{error.message}</p>}
        <div className="confirm-dialog-actions">
          <button ref={cancelRef} className="secondary-button" disabled={pending} onClick={onCancel}>Cancel</button>
          <button className="primary-button" disabled={pending} onClick={() => void onConfirm()}>{pending ? "Marking…" : "Mark all read"}</button>
        </div>
      </section>
    </div>
  );
}

function StartupRoute({ loading, startScreen }: { loading: boolean; startScreen?: string }) {
  if (loading) return <RouteLoading />;
  if (startScreen === "new_chat") {
    return <Navigate to="/chat" replace />;
  }
  return <Navigate to="/notes" replace state={{ openNotesBrowser: true }} />;
}

function noteIdFromPath(pathname: string) {
  const match = pathname.match(/^\/notes\/([^/]+)\/?$/);
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function RouteLoading() {
  return (
    <div className="route-loading" role="status" aria-live="polite">
      <div className="loading-bar" />
      <span>Loading workspace...</span>
    </div>
  );
}

function Wordmark() {
  return (
    <div className="wordmark" aria-label="Notey-G">
      <span className="wordmark-mark">N</span>
      <span>notey-g</span>
    </div>
  );
}

function kindOf(item: RecentItem) {
  return item.kind || item.type || "note";
}

function RecentRow({
  item,
  close,
  guardedMetadataEnabled
}: {
  item: RecentItem;
  close: () => void;
  guardedMetadataEnabled: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const kind = kindOf(item);
  const isThread = kind === "thread" || kind === "chat";
  const title = item.title?.trim() || (isThread ? "New chat" : "Untitled");
  const pin = useMutation({
    mutationFn: async () => {
      if (isThread) await api.updateChatThread(item.id, { pinned: !item.pinned });
      else {
        const guardedMutation = guardedRecentPinMutation(item, guardedMetadataEnabled);
        if (guardedMutation) {
          await api.updateNoteMetadata(item.id, guardedMutation.expectedRevision, guardedMutation.patch);
        } else {
          await api.updateNote(item.id, { pinned: !item.pinned });
        }
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recent"] })
  });
  const archive = useMutation({
    mutationFn: () => api.updateChatThread(item.id, { archived: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recent"] })
  });
  const remove = useMutation({
    mutationFn: () => api.deleteChatThread(item.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recent"] })
  });
  const busy = pin.isPending || archive.isPending || remove.isPending;

  function onAction(event: MouseEvent, action: "pin" | "archive" | "delete") {
    event.preventDefault();
    event.stopPropagation();
    if (action === "pin") void pin.mutateAsync();
    if (action === "archive") void archive.mutateAsync();
    if (action === "delete" && window.confirm(`Delete "${title}"?`)) void remove.mutateAsync();
  }

  return (
    <div className={`recent-row ${item.pinned ? "pinned" : ""}`}>
      <button className="recent-open" onClick={() => {
        navigate(isThread ? `/chat/${item.id}` : `/notes/${item.id}`);
        // Navigate while the row is still mounted, then dismiss the modal
        // drawer. Closing first can synchronously remove the event owner on
        // compact/browser-controlled surfaces and leave the route unchanged.
        close();
      }}>
        {isThread ? <MessageCircle size={17} /> : <StickyNote size={17} />}
        <span>
          <strong>{title}</strong>
          <small>{isThread ? "Chat" : "Note"}</small>
        </span>
        {Boolean(item.unreadCount) && <em>{item.unreadCount}</em>}
      </button>
      <div className="recent-actions">
        <button className="icon-button tiny" disabled={busy} aria-label={item.pinned ? "Unpin" : "Pin"} onClick={(event) => onAction(event, "pin")}>
          <Pin size={13} fill={item.pinned ? "currentColor" : "none"} />
        </button>
        {isThread && (
          <>
            <button className="icon-button tiny" disabled={busy} aria-label="Archive chat" onClick={(event) => onAction(event, "archive")}>
              <Archive size={13} />
            </button>
            <button className="icon-button tiny danger" disabled={busy} aria-label="Delete chat" onClick={(event) => onAction(event, "delete")}>
              <Trash2 size={13} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SearchHome() {
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const notes = useQuery({
    queryKey: ["search", "notes", trimmed],
    queryFn: () => api.search(trimmed),
    enabled: trimmed.length > 1
  });
  const chats = useQuery({
    queryKey: ["search", "chats", trimmed],
    queryFn: () => api.listChatThreads(trimmed, 8),
    enabled: trimmed.length > 1
  });
  const navigate = useNavigate();
  const loading = notes.isLoading || chats.isLoading;
  const noteResults = notes.data?.results || [];
  const chatResults = chats.data?.threads || [];

  return (
    <section className="search-page">
      <label className="search-box">
        <Search size={20} />
        <input
          autoFocus
          placeholder="Search notes and chats"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {query && (
          <button className="icon-button tiny" aria-label="Clear search" onClick={() => setQuery("")}>
            <X size={15} />
          </button>
        )}
      </label>
      <div className="search-results">
        {!trimmed && <SearchEmpty title="Start typing" body="Find notes, wikilinks, tags, and chat threads from one native app search." />}
        {trimmed.length === 1 && <SearchEmpty title="Keep typing" body="Search starts after two characters." />}
        {loading && <SearchEmpty title="Searching" body="Checking notes and conversations..." />}
        {trimmed.length > 1 && !loading && !noteResults.length && !chatResults.length && (
          <SearchEmpty title="No results" body="Try a different word or phrase." />
        )}
        {noteResults.length > 0 && (
          <ResultSection title="Notes">
            {noteResults.map((note) => (
              <button key={note.id} className="result-row" onClick={() => navigate(`/notes/${note.id}`)}>
                <StickyNote size={18} />
                <span>
                  <strong>{note.title || "Untitled"}</strong>
                  <small>{note.path || "Note"}{note.snippet ? ` · ${note.snippet}` : ""}</small>
                </span>
                <ChevronRight size={16} />
              </button>
            ))}
          </ResultSection>
        )}
        {chatResults.length > 0 && (
          <ResultSection title="Chats">
            {chatResults.map((thread) => (
              <button key={thread.id} className="result-row" onClick={() => navigate(`/chat/${thread.id}`)}>
                <MessageCircle size={18} />
                <span>
                  <strong>{thread.title || "New chat"}</strong>
                  <small>{thread.unreadCount ? `${thread.unreadCount} unread` : "Conversation"}</small>
                </span>
                <ChevronRight size={16} />
              </button>
            ))}
          </ResultSection>
        )}
      </div>
    </section>
  );
}

function SearchEmpty({ title, body }: { title: string; body: string }) {
  return (
    <div className="search-empty">
      <Search size={26} />
      <h2>{title}</h2>
      <p>{body}</p>
    </div>
  );
}

function ResultSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="result-section">
      <h2>{title}</h2>
      <div>{children}</div>
    </section>
  );
}
